/* Prices always come from Shopify's cart response and rendered section. */
(() => {
  'use strict';
  const instances = new WeakMap();
  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const identity = row => JSON.stringify([
    row.dataset.variantId,
    Object.entries(JSON.parse(row.dataset.properties || '{}') || {}).sort(([a], [b]) => a.localeCompare(b)),
    row.dataset.sellingPlan || ''
  ]);
  class Cart {
    constructor(root) {
      this.root = root;
      this.disabled = new Map();
      this.status = root.querySelector('[data-cart-status]');
      this.error = root.querySelector('[data-cart-error]');
      this.delay = Math.min(1000, Math.max(200, Number(root.dataset.delay) || 400));
      this.hideUpdate();
      const change = event => {
        if (!event.target.matches('[data-cart-quantity]') || this.busy) return;
        event.target.setCustomValidity('');
        clearTimeout(this.timer);
        // A blank field is allowed while the customer is typing.
        if (event.target.value !== '') this.timer = setTimeout(() => this.flush(), this.delay);
      };
      root.addEventListener('input', change);
      root.addEventListener('change', change);
      root.addEventListener('click', event => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        if (this.busy) return;
        const input = remove.closest('[data-cart-line]').querySelector('[data-cart-quantity]');
        input.value = '0';
        input.setCustomValidity('');
        this.flush();
      });
      root.addEventListener('submit', event => {
        if (this.allowSubmit || (event.submitter?.name === 'update' && !event.submitter.hidden)) return;
        const checkout = event.submitter?.matches('[data-cart-checkout]');
        if (!checkout && event.submitter && event.submitter.name !== 'update') return;
        // Enter in a quantity field updates the cart, never starts payment.
        if (!checkout) { event.preventDefault(); this.flush(); return; }
        if (!this.busy && !this.hasChanges()) return;
        event.preventDefault();
        if (this.checkoutQueued) return;
        this.checkoutQueued = true;
        this.flush().then(ok => {
          if (!ok) return;
          const button = root.querySelector('[data-cart-checkout]');
          const form = button?.form;
          if (form) {
            this.allowSubmit = true;
            try { form.requestSubmit(button); } finally { this.allowSubmit = false; }
          }
        }).finally(() => { this.checkoutQueued = false; });
      });
    }
    hideUpdate() {
      const button = this.root.querySelector('[data-cart-update]');
      if (button) button.hidden = true;
    }
    hasChanges() {
      return all(this.root, '[data-cart-quantity]').some(input => input.value !== input.dataset.quantity);
    }
    collect() {
      const changes = [];
      for (const input of all(this.root, '[data-cart-quantity]')) {
        const quantity = Number(input.value);
        const min = Number(input.dataset.min) || 1;
        const step = Number(input.dataset.step) || 1;
        const max = input.max === '' ? Infinity : Number(input.max);
        const valid = input.value !== '' && Number.isSafeInteger(quantity) && quantity >= 0 && quantity <= max && (quantity === 0 || (quantity >= min && (quantity - min) % step === 0));
        if (!valid) {
          input.setCustomValidity(`Adedi 0 veya en az ${min} olacak şekilde, ${step} artışlarla girin.${Number.isFinite(max) ? ` En fazla ${max} adet seçebilirsiniz.` : ''}`);
          input.reportValidity();
          return null;
        }
        input.setCustomValidity('');
        if (quantity !== Number(input.dataset.quantity)) {
          const row = input.closest('[data-cart-line]');
          changes.push({key: row.dataset.key, identity: identity(row), quantity});
        }
      }
      return changes;
    }
    lock() {
      this.root.setAttribute('aria-busy', 'true');
      all(this.root, 'input,textarea,select,button').forEach(control => {
        if (!this.disabled.has(control)) this.disabled.set(control, control.disabled);
        control.disabled = true;
      });
      all(this.root, '[data-cart-remove]').forEach(link => link.setAttribute('aria-disabled', 'true'));
    }
    unlock() {
      this.root.removeAttribute('aria-busy');
      this.disabled.forEach((wasDisabled, control) => { control.disabled = wasDisabled; });
      this.disabled.clear();
      all(this.root, '[data-cart-remove]').forEach(link => link.removeAttribute('aria-disabled'));
    }
    async fetchWithTimeout(url, options) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 20000);
      try {
        const response = await fetch(url, {...options, credentials: 'same-origin', signal: abort.signal});
        const text = await response.text();
        return {response, text};
      } finally { clearTimeout(timeout); }
    }
    render(html) {
      if (typeof html !== 'string') throw new Error(this.root.dataset.errorLabel);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-cart-root]');
      if (!next?.querySelector('[data-cart-content]')) throw new Error(this.root.dataset.errorLabel);
      const oldLines = this.root.querySelector('[data-cart-lines]');
      const nextLines = next.querySelector('[data-cart-lines]');
      if (oldLines && nextLines) {
        oldLines.replaceChildren(...nextLines.childNodes);
        this.root.querySelector('[data-cart-pricing]').replaceChildren(...next.querySelector('[data-cart-pricing]').childNodes);
      } else {
        const content = this.root.querySelector('[data-cart-content]');
        content.replaceChildren(...next.querySelector('[data-cart-content]').childNodes);
      }
      this.root.dataset.count = next.dataset.count;
      all(document, '.cart-count').forEach(node => { node.textContent = next.dataset.count; });
      this.hideUpdate();
      if (this.busy) this.lock();
      document.dispatchEvent(new CustomEvent('firelice:cart:updated', {detail: {itemCount: Number(next.dataset.count)}}));
    }
    async refresh() {
      const url = new URL(location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.root.dataset.sectionId);
      url.searchParams.set('_', String(Date.now()));
      const {response, text} = await this.fetchWithTimeout(url, {headers: {Accept: 'text/html'}, cache: 'no-store'});
      if (!response.ok) throw new Error(this.root.dataset.errorLabel);
      this.render(text);
    }
    findRow(change) {
      const rows = all(this.root, '[data-cart-line]');
      const sameKey = rows.find(row => row.dataset.key === change.key);
      if (sameKey) return sameKey;
      // Discounts can change Shopify line keys. Only remap an unambiguous match.
      const matches = rows.filter(row => identity(row) === change.identity);
      return matches.length === 1 ? matches[0] : null;
    }
    async apply(changes) {
      let adjusted = false;
      try {
        const route = window.Shopify?.routes?.root || '/';
        const endpoint = new URL(route.replace(/\/?$/, '/') + 'cart/change.js', location.origin);
        if (endpoint.origin !== location.origin) throw new Error(this.root.dataset.errorLabel);
        for (const change of changes) {
          const row = this.findRow(change);
          if (!row) throw new Error('Sepetteki ürünler değişti. Lütfen güncel sepeti kontrol edin.');
          if (Number(row.querySelector('[data-cart-quantity]').dataset.quantity) === change.quantity) continue;
          const {response, text} = await this.fetchWithTimeout(endpoint, {
            method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({id: row.dataset.key, quantity: change.quantity, sections: [this.root.dataset.sectionId], sections_url: location.pathname})
          });
          const data = JSON.parse(text);
          if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : this.root.dataset.errorLabel);
          const html = data.sections?.[this.root.dataset.sectionId];
          // A section-rendering failure must never repeat the cart mutation.
          if (typeof html === 'string') this.render(html); else await this.refresh();
          const result = this.findRow(change);
          const actual = result ? Number(result.querySelector('[data-cart-quantity]').dataset.quantity) : 0;
          if (actual !== change.quantity) adjusted = true;
        }
        this.status.textContent = adjusted ? this.root.dataset.adjustedLabel : this.root.dataset.updatedLabel;
        return !adjusted;
      } catch (failure) {
        // Also refresh after ambiguous network/stock errors: the server may have accepted a change.
        try { await this.refresh(); } catch (_) { /* Keep the native update form usable. */ }
        this.status.textContent = '';
        this.error.textContent = ['TypeError', 'SyntaxError', 'AbortError'].includes(failure.name) ? this.root.dataset.errorLabel : failure.message;
        this.error.hidden = false;
        const fallback = this.root.querySelector('[data-cart-update]');
        if (fallback) fallback.hidden = false;
        return false;
      }
    }
    flush() {
      clearTimeout(this.timer);
      if (this.busy) return this.pending;
      const changes = this.collect();
      if (!changes) return Promise.resolve(false);
      if (!changes.length) return Promise.resolve(true);
      const active = document.activeElement;
      const focus = active?.matches('[data-cart-quantity]') ? identity(active.closest('[data-cart-line]')) : null;
      this.busy = true;
      this.error.hidden = true;
      this.status.textContent = this.root.dataset.updatingLabel;
      this.lock();
      this.pending = this.apply(changes).finally(() => {
        this.busy = false;
        this.unlock();
        if (focus) {
          const row = all(this.root, '[data-cart-line]').find(item => identity(item) === focus);
          row?.querySelector('[data-cart-quantity]')?.focus({preventScroll: true});
        }
      });
      return this.pending;
    }
    destroy() { clearTimeout(this.timer); }
  }
  function initialize(scope = document) {
    all(scope, '[data-cart-root][data-auto-update="true"]').forEach(root => {
      if (!instances.has(root)) instances.set(root, new Cart(root));
    });
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    all(event.target, '[data-cart-root]').forEach(root => instances.get(root)?.destroy());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once: true});
  else initialize();
})();
